import type { ReploElementMetadata } from "schemas/generated/element";

import * as React from "react";

import { useV1ErrorToast } from "@v1/hooks/useV1ErrorToast";
import {
  INVALID_ELEMENT_NAME_CHARACTERS_REGEX,
  MAX_SECTION_NAME_LENGTH,
} from "@v1/utils/element";
import { elementRouter, elementUtils } from "@v2/trpc/routes/element";
import { slugify } from "replo-utils/lib/string";

export function useElementName({
  element,
  initialName,
}: {
  element?: ReploElementMetadata;
  initialName?: string;
}): {
  elementName: string;
  setElementName: (name: string) => void;
  isEditingName: boolean;
  setIsEditingName: (isEditing: boolean) => void;
  handleUpdateElementName: (name: string) => Promise<void>;
  handleBlur: () => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  isInvalidElementName: boolean;
} {
  const [isEditingName, setIsEditingName] = React.useState(false);
  // NOTE (Patrick, 2026-02-19): Only used as an editing buffer when isEditingName
  // is true. When not editing, we derive the display name from element?.name
  // directly to avoid a useEffect sync loop with React 19's stricter re-rendering.
  const [editingBuffer, setEditingBuffer] = React.useState(initialName);
  const [isInvalidElementName, setisInvalidElementName] = React.useState(false);
  const errorToast = useV1ErrorToast();

  const elementName = isEditingName ? editingBuffer : element?.name;

  const handleSetIsEditingName = React.useCallback(
    (isEditing: boolean) => {
      if (isEditing && element) {
        setEditingBuffer(element.name);
      }
      setIsEditingName(isEditing);
    },
    [element],
  );

  const handleChangeElementName = React.useCallback(
    (name: string) => {
      if (element?.type === "shopifySection") {
        if (name.length > MAX_SECTION_NAME_LENGTH) {
          if (!isInvalidElementName) {
            errorToast(
              `Section names must be less than ${MAX_SECTION_NAME_LENGTH} characters.`,
              "Section name too long",
              "error.element.update",
              {
                elementDetail: JSON.stringify(element),
              },
            );
          }
          setisInvalidElementName(true);
        } else {
          setisInvalidElementName(false);
        }
      }

      setEditingBuffer(name);
    },
    [element, isInvalidElementName, errorToast],
  );

  const updateElementMetadata = elementRouter.updateElementMetadata.useMutation(
    {
      onMutate: async (variables) => {
        // Patrick (2025-08-21) TODO: Update this to remove the type assertion whenever trpc is fixed
        // https://linear.app/replo/issue/REPL-18866/update-type-assertions-when-trpc-fixes-zod-v4-integratoin
        const { element: elementToUpdate } = variables as {
          element: ReploElementMetadata;
        };
        // NOTE (Ben O., 2025-02-10): to keep the element name in sync in the editor
        // we cancel the outgoing refetch of the element metadata and snapshot the
        // previous value to use in case of error. We also set the trpc call to
        // optimistic update to the new value.
        if (element) {
          await elementUtils.getElementMetadataById.cancel(element.id);
          await elementUtils.listAllElementsMetadata.cancel({
            projectId: element.projectId,
          });

          elementUtils.getElementMetadataById.setData(
            element.id,
            (old: any) => {
              return old ? { ...old, name: elementToUpdate.name! } : old;
            },
          );
          elementUtils.listAllElementsMetadata.setData(
            { projectId: element.projectId },
            (old) => {
              if (!old) {
                return old;
              }
              return old.map((el) => {
                return el.id === elementToUpdate.id
                  ? { ...el, name: elementToUpdate.name! }
                  : el;
              });
            },
          );

          return { previousName: element.name };
        }
      },
      onError: (_err, _variables, context) => {
        // Note (Ben O. 2025-02-06): If the update fails, we roll back to the previous value
        if (element && context?.previousName) {
          elementUtils.getElementMetadataById.setData(element.id, {
            ...element,
            name: context.previousName,
          });
          elementUtils.listAllElementsMetadata.setData(
            { projectId: element.projectId },
            (old) => {
              if (!old) {
                return old;
              }
              return old.map((el) => {
                return el.id === element.id
                  ? { ...el, name: context.previousName }
                  : el;
              });
            },
          );
        }
        errorToast(
          "Please try again.",
          "Failed to update element name",
          "error.element.update",
          {
            elementDetail: JSON.stringify(element),
          },
        );
      },
      onSuccess: () => {
        if (element) {
          void elementUtils.listAllElementsMetadata.invalidate();
          void elementUtils.getElementMetadataById.invalidate(element.id);
          setIsEditingName(false);
        }
      },
    },
  );

  const handleUpdateElementName = React.useCallback(
    async (name: string) => {
      if (!element) {
        setIsEditingName(false);
        errorToast(
          "Please try again.",
          "Failed to update element name",
          "error.element.update",
          {
            elementDetail: JSON.stringify(element),
          },
        );
        return;
      }

      const newName = name.replace(INVALID_ELEMENT_NAME_CHARACTERS_REGEX, "");

      if (newName === element.name) {
        setIsEditingName(false);
        return;
      }

      if (isInvalidElementName) {
        errorToast(
          "Please rename the page.",
          "Invalid page name",
          "error.element.update",
          {
            elementDetail: JSON.stringify(element),
          },
        );
        return;
      }

      // Update page path only when initially creating a new element
      const shopifyPagePath =
        element.type === "page" &&
        element.name.toLowerCase().includes("untitled")
          ? slugify(newName)
          : element.shopifyPagePath;

      return updateElementMetadata.mutateAsync({
        element: {
          ...element,
          name: newName,
          shopifyPagePath,
        },
      });
    },
    [element, updateElementMetadata, isInvalidElementName, errorToast],
  );

  const handleBlur = React.useCallback(() => {
    setIsEditingName(false);
    if (elementName) {
      void handleUpdateElementName(elementName);
    }
  }, [elementName, handleUpdateElementName]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === "Escape") {
        setIsEditingName(false);
        if (elementName) {
          void handleUpdateElementName(elementName);
        }
      }
    },
    [elementName, handleUpdateElementName],
  );

  return {
    elementName: elementName ?? "",
    setElementName: handleChangeElementName,
    isEditingName,
    setIsEditingName: handleSetIsEditingName,
    handleUpdateElementName,
    handleKeyDown,
    handleBlur,
    isInvalidElementName,
  };
}
